import { TabSummary, WorkflowCheckerIssue } from "workflowEditorV3/WorkflowEditorV3";

import { AssetViewScope } from "domain/asset-management/assets";
import { apiGatewayService, ApiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { AssetFilterDto, toAssetFilterDto } from "services/asset-management/AssetService";

const AI_AGENT_PATH = "/api/v1/ai-agent";

const snakeToCamelCase = (snakeCase: string): string =>
    snakeCase.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());

interface SessionHistoryDto {
    session_id: string | null;
    messages: Array<{ role: "user" | "assistant"; content: string }>;
    common_license_tokens_consumed: number;
}

interface SessionDto {
    session_id: string;
    created: string;
    name: string | null;
}

interface PostFeedbackDto {
    rating: "positive" | "negative";
    reason?: string;
    comment?: string;
}

interface RenameSessionDto {
    name: string;
}

interface AssetViewScopeDto {
    group_uuid: string;
    filters: AssetFilterDto[];
    filter_logic?: string;
    start_date?: string;
    end_date?: string;
}

const toAssetViewScopeDto = (scope: AssetViewScope): AssetViewScopeDto => ({
    group_uuid: scope.groupUuid,
    filters: scope.filters.map(toAssetFilterDto),
    filter_logic: scope.filterLogic,
    start_date: scope.startDate,
    end_date: scope.endDate,
});

interface SendMessageDto {
    message: string;
    current_view: string;
    locale: string;
    timezone: string;
    current_workflow_name?: string;
    current_workflow_json?: string;
    current_workflow_profile?: string;
    current_workflow_profile_version?: string;
    current_errors?: WorkflowCheckerIssue[];
    all_tabs?: TabSummary[];
    asset_view?: AssetViewScopeDto;
    is_automatic: boolean;
    session_id?: string;
}

// What a turn produced, reported by whichever event ends it. An error event is terminal, so a
// turn that failed after doing billable work carries these too; both are absent when it was
// rejected before it had them.
interface TurnOutcomeDto {
    session_id?: string;
    common_license_tokens_consumed?: number;
}

type StreamChunkDto =
    | string
    | { type: "done"; session_id: string; common_license_tokens_consumed: number }
    | ({ type: "error"; code: string } & TurnOutcomeDto)
    | { type: "tool_status"; tool_name: string };

interface TurnOutcome {
    sessionId?: string;
    consumedLicenseTokens?: number;
}

function toTurnOutcome(chunk: TurnOutcomeDto): TurnOutcome {
    return {
        sessionId: chunk.session_id,
        consumedLicenseTokens: chunk.common_license_tokens_consumed,
    };
}

export interface Session {
    sessionId: string;
    createdAt: string;
    name?: string;
}

export interface SessionHistory {
    sessionId?: string;
    messages: Array<{ role: "user" | "assistant"; content: string }>;
    licensesConsumed: number;
}

class AiAgentService {
    public async fetchSessions(abortController?: AbortController): Promise<Session[]> {
        const data = await apiGatewayService.invokeApi<never, { cursor: string; sessions: SessionDto[] }>(
            `${AI_AGENT_PATH}/sessions`,
            "GET",
            undefined,
            { apiType: ApiType.ARTHUR, abortSignal: abortController?.signal }
        );

        return data.sessions.map((session) => ({
            sessionId: session.session_id,
            createdAt: session.created,
            name: session.name ?? undefined,
        }));
    }

    public async fetchSessionById(sessionId: string, abortController?: AbortController): Promise<SessionHistory> {
        const data = await apiGatewayService.invokeApi<never, SessionHistoryDto>(
            `${AI_AGENT_PATH}/sessions/${sessionId}`,
            "GET",
            undefined,
            { apiType: ApiType.ARTHUR, abortSignal: abortController?.signal }
        );
        return {
            sessionId: data.session_id ?? undefined,
            messages: data.messages,
            licensesConsumed: data.common_license_tokens_consumed,
        };
    }

    public async deleteSession(sessionId: string, abortController?: AbortController): Promise<void> {
        await apiGatewayService.invokeApi<never, void>(`${AI_AGENT_PATH}/sessions/${sessionId}`, "DELETE", undefined, {
            apiType: ApiType.ARTHUR,
            emptyResponse: true,
            abortSignal: abortController?.signal,
        });
    }

    public async renameSession(sessionId: string, name: string, abortController?: AbortController): Promise<void> {
        await apiGatewayService.invokeApi<RenameSessionDto, void>(
            `${AI_AGENT_PATH}/sessions/${sessionId}`,
            "POST",
            { name },
            { apiType: ApiType.ARTHUR, emptyResponse: true, abortSignal: abortController?.signal }
        );
    }

    public async postFeedback(
        sessionId: string,
        rating: "positive" | "negative",
        reason?: string,
        comment?: string,
        abortController?: AbortController
    ): Promise<void> {
        await apiGatewayService.invokeApi<PostFeedbackDto, void>(
            `${AI_AGENT_PATH}/sessions/${sessionId}/feedback`,
            "POST",
            { rating, reason, comment },
            { apiType: ApiType.ARTHUR, emptyResponse: true, abortSignal: abortController?.signal }
        );
    }

    public async *sendMessageStream(
        prompt: string,
        currentView: string,
        locale: string,
        timezone: string,
        currentWorkflowName: string | undefined,
        currentWorkflow: string | undefined,
        currentWorkflowProfile: string | undefined,
        currentWorkflowProfileVersion: string | undefined,
        currentErrors: WorkflowCheckerIssue[] | undefined,
        allTabs: TabSummary[] | undefined,
        isAutomatic: boolean,
        sessionId?: string,
        abortController?: AbortController,
        assetViewScope?: AssetViewScope
    ): AsyncGenerator<
        | { type: "token"; token: string }
        | { type: "done"; sessionId: string; consumedLicenseTokens: number }
        | ({ type: "error"; code: string } & TurnOutcome)
        | { type: "toolStatus"; toolName: string }
    > {
        const url = ApiGatewayService.getApiUrl(`${AI_AGENT_PATH}/stream`, ApiType.ARTHUR);
        const body: SendMessageDto = {
            message: prompt,
            current_view: currentView,
            locale,
            timezone,
            current_workflow_name: currentWorkflowName,
            current_workflow_json: currentWorkflow,
            current_workflow_profile: currentWorkflowProfile,
            current_workflow_profile_version: currentWorkflowProfileVersion,
            current_errors: currentErrors,
            all_tabs: allTabs,
            asset_view: assetViewScope && toAssetViewScopeDto(assetViewScope),
            is_automatic: isAutomatic,
            session_id: sessionId,
        };
        const response = await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
            body: JSON.stringify(body),
            signal: abortController?.signal,
        });

        if (!response.ok) {
            throw new Error(`Stream request failed with status ${response.status}`);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() ?? "";

                for (const line of lines) {
                    if (line.startsWith("data: ")) {
                        const chunk = JSON.parse(line.slice(6).trim()) as StreamChunkDto;
                        if (typeof chunk === "string") {
                            yield { type: "token", token: chunk };
                        } else if (chunk.type === "error") {
                            yield { type: "error", code: chunk.code, ...toTurnOutcome(chunk) };
                        } else if (chunk.type === "done") {
                            yield {
                                type: "done",
                                sessionId: chunk.session_id,
                                consumedLicenseTokens: chunk.common_license_tokens_consumed,
                            };
                        } else if (chunk.type === "tool_status") {
                            yield { type: "toolStatus", toolName: snakeToCamelCase(chunk.tool_name) };
                        }
                    }
                }
            }
        } catch (error) {
            if (abortController?.signal.aborted) {
                return;
            }
            throw error;
        } finally {
            await reader.cancel().catch(() => undefined);
        }
    }
}

export const aiAgentService = new AiAgentService();
